import {
  TuningProfileDetail,
  TuningProfileField,
  TuningProfileSettingsUpdate,
} from '../../../models/TuningProfile.model'

// the editor's working copy, as typed: one entry for the trail system (SYSTEM_ROW) and one per trail id
export type TuningProfileDraft = Record<string, Partial<Record<TuningProfileField, string>>>

export const SYSTEM_ROW = 'system'

// the trail system's own values, in the same column order as the trails' (it has no resistance)
export const tuningProfileSystemFields: TuningProfileField[] = [
  'precipitationLookbackDays',
  'saturationModifier',
  'dryTimeModifier',
]

const toText = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value))

export const draftFromDetail = (detail: TuningProfileDetail): TuningProfileDraft => {
  const draft: TuningProfileDraft = {
    [SYSTEM_ROW]: {
      precipitationLookbackDays: toText(detail.system.precipitationLookbackDays),
      saturationModifier: toText(detail.system.saturationModifier),
      dryTimeModifier: toText(detail.system.dryTimeModifier),
    },
  }
  detail.trails.forEach((trail) => {
    draft[String(trail.trailId)] = {
      weatherResistance: toText(trail.values.weatherResistance),
      precipitationLookbackDays: toText(trail.values.precipitationLookbackDays),
      saturationModifier: toText(trail.values.saturationModifier),
      dryTimeModifier: toText(trail.values.dryTimeModifier),
    }
  })
  return draft
}

/**
 * @description Why a typed value can't be saved, matching what the API accepts: resistance is a whole
 * number from 1 to 20 and can't be blank, lookback is a whole number of days, and the modifiers can't
 * be negative. Blank means the value inherits.
 */
export const tuningValueError = (field: TuningProfileField, text: string | undefined): string | undefined => {
  const trimmed = (text ?? '').trim()
  if (!trimmed) {
    return field === 'weatherResistance' ? 'Resistance is required' : undefined
  }
  const value = Number(trimmed)
  if (!Number.isFinite(value)) {
    return 'Enter a number'
  }
  if (field === 'weatherResistance' && !(Number.isInteger(value) && value >= 1 && value <= 20)) {
    return 'Whole number from 1 to 20'
  }
  if (field === 'precipitationLookbackDays' && !(Number.isInteger(value) && value >= 0)) {
    return 'Whole number of days'
  }
  if (value < 0) {
    return "Can't be negative"
  }
  return undefined
}

export type TuningProfileDraftChange = { row: string; field: TuningProfileField }

export const draftChanges = (draft: TuningProfileDraft, original: TuningProfileDraft): TuningProfileDraftChange[] =>
  Object.keys(draft).flatMap((row) =>
    (Object.keys(draft[row]) as TuningProfileField[])
      .filter((field) => (draft[row][field] ?? '').trim() !== (original[row]?.[field] ?? '').trim())
      .map((field) => ({ row, field })),
  )

// only what changed goes to the API; a blank value is sent as null so it inherits
export const settingsUpdateFromDraft = (
  draft: TuningProfileDraft,
  changes: TuningProfileDraftChange[],
): TuningProfileSettingsUpdate => {
  const update: TuningProfileSettingsUpdate = {}
  changes.forEach(({ row, field }) => {
    const text = (draft[row][field] ?? '').trim()
    const value = text === '' ? null : Number(text)
    if (row === SYSTEM_ROW) {
      update.system = { ...update.system, [field]: value }
    } else {
      update.trails = { ...update.trails, [row]: { ...update.trails?.[row], [field]: value } }
    }
  })
  return update
}
